import { useLocation, useNavigate } from "react-router-dom";
import { useGoBack } from "../../customHooks/useGoBack";
import { Card, Col, Flex, Form, Grid, Input, message, Row, Switch, Tooltip } from "antd";
import CustomDateTimePicker from "../../common/CustomDateTimePicker";
import { CurrencyCodeSelect, SupplierOrgSelect } from "../../helpers/common";
import WarehouseSelect from "../../components/WarehouseSelect";
import { PURCHASE_ORDER_ROOT } from "../RouteConstants";
import { useMutation, useQuery } from "@tanstack/react-query";
import PurchaseOrderService from "../../services/PurchaseOrder/PurchaseOrderService";
import { useEffect, useState } from "react";
import { queryClient } from "../../react-query-client";
import AppButton from "../../common/AppButton";
import dayjs, { } from "../../helpers/dayjsSetup";
import PurchaseOrderUpdateStatusButtons from "../../components/PurchaseOrderUpdateStatusButtons";
import PurchaseOrderDocumentStatusPage from "./PurchaseOrderDocumentStatusPage";
import ReactCountryFlag from "react-country-flag";
import PurchaseOrderDelete from "../../components/PurchaseOrderDelete";

const { useBreakpoint } = Grid;

const PurchaseOrderDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const [purchaseOrderLineItems, setPurchaseOrderLineItems] = useState();

  const screenSize = useBreakpoint();

  const isSmallPhone = !screenSize["sm"];

  let purchaseOrderId = null;
  if (location.state && location.state?.purchaseOrderId) {
    purchaseOrderId = location.state.purchaseOrderId;
  }

  const title = !purchaseOrderId ? "New Purchase Order" : "Purchase Order Details";
  const [form] = Form.useForm();

  const { mutate: createPurchaseOrder } = useMutation({
    mutationFn: async (data) => await PurchaseOrderService.createPurchaseOrder(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrderListTabCounts"] });
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrderList"] });
      message.success("Purchase order saved successfully!");
      navigate(`${PURCHASE_ORDER_ROOT}`);
    },
    onError: async (error) => {
      message.error(`Failed to save purchase order. ${error.response?.data?.message}, Please try again.`);
    },
  });

  const { mutate: updatePurchaseOrder } = useMutation({
    mutationFn: async (data) => await PurchaseOrderService.updatePurchaseOrder(purchaseOrderId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrders", purchaseOrderId] });
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrderListTabCounts"] });
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrderList"] });
      message.success("Purchase order updated successfully!");
      goBack();
    },
    onError: (error) => {
      message.error(`Failed to update purchase order. ${error.response?.data?.message}, Please try again.`);
    },
  });

  const { mutate: deletePurchaseOrderItem } = useMutation({
    mutationFn: async ({ itemId, purchaseOrderId }) => await PurchaseOrderService.deletePurchaseOrderLineItemAsync(itemId, purchaseOrderId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrderListTabCounts"] });
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrderList"] });
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrders", purchaseOrderId] });
      await message.success("Purchase order item deleted successfully.");
    },
    onError: async (error) => {
      await message.error(`Failed to delete purchase order item. ${error.response?.data?.message}, Please try again.`);
    }
  });

  const { data: poStatuses } = useQuery({
    queryKey: ["trackingStatuses"],
    queryFn: async () => await PurchaseOrderService.getTrackingStatuses(),
    enabled: !!purchaseOrderId,
  });

  const { data: currentPo } = useQuery({
    queryKey: ["purchaseOrders", purchaseOrderId],
    queryFn: async () => await PurchaseOrderService.getPurchaseOrderById(purchaseOrderId),
    enabled: !!purchaseOrderId,
    select: (data) => {
      if (!data) return data;

      return {
        ...data,
        poDate: dayjs(data.poDate),
        qaDate: data.qaDate ? dayjs(data.qaDate) : null,
        cargoReadyDate: data.cargoReadyDate ? dayjs(data.cargoReadyDate) : null,
        etaDate: data.etaDate ? dayjs(data.etaDate) : null,
      };
    },
  });

  useEffect(() => {
    if (currentPo) {
      form.setFieldsValue(currentPo);
    } else {
      form.resetFields();
      form.setFieldValue(["poTotal", "currencyCode"], "USD");
    }
  }, [currentPo, form]);

  useEffect(() => {
    setPurchaseOrderLineItems(currentPo?.purchaseOrderLineItems || []);
  }, [currentPo?.purchaseOrderLineItems]);

  const handleCancel = () => goBack();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      values.poDate = values.poDate?.format("YYYY-MM-DDTHH:mm");
      values.qaDate = values.qaDate?.format("YYYY-MM-DDTHH:mm");
      values.cargoReadyDate = values.cargoReadyDate?.format("YYYY-MM-DDTHH:mm");
      values.etaDate = values.etaDate?.format("YYYY-MM-DDTHH:mm");

      if (purchaseOrderId) {
        values.ID = purchaseOrderId;
        values.purchaseOrderLineItems = purchaseOrderLineItems;
        updatePurchaseOrder(values);
      } else {
        createPurchaseOrder(values);
      }
    } catch {
      // Do nothing, let the form handle the error messages
    }
  };

  const handlePoLineItemChange = (index, field, value) => {
    const updated = [...purchaseOrderLineItems];
    updated[index][field] = value;
    setPurchaseOrderLineItems(updated);
  };

  const handleAddNewPoLineItem = () => {
    const newItem = {
      id: 0,
      purchaseOrderId: purchaseOrderId,
      sku: "",
      description: "",
      poQty: 0,
      supplyQty: 0,
      unitCost: 0,
    };
    setPurchaseOrderLineItems([...purchaseOrderLineItems, newItem]);
  };

  const handlePoLineItemDelete = (item) => {
    const updatedItems = purchaseOrderLineItems.filter(i => i !== item);
    setPurchaseOrderLineItems(updatedItems);
    if (item.id) {
      deletePurchaseOrderItem({ itemId: item.id, purchaseOrderId });
    }
  }

  const handleImportPurchaseOrderLineItems = (newItems) => {
    newItems.forEach(item => item.purchaseOrderId = purchaseOrderId);
    setPurchaseOrderLineItems(prev => [...prev, ...newItems]);
  }

  const currentStatus = poStatuses?.find(pos => pos.enum === currentPo?.statusID)?.name;

  return (
    <div className="px-8 mb-4 adaptive-text">
      <Flex justify="space-between" align="center" className="w-full my-2  mx-2 font-inter">
        <span className="header-title">
          {title}
        </span>
      </Flex>
      <Form form={form} layout="vertical">
        <Row gutter={[8, 8]} className="mb-4">
          <Col span={12} xs={24} md={24} lg={12}>
            <Card className="card-form flex-1 h-full">
              <Flex gap={8}>
                <Form.Item label="Order Number"
                  className="flex-1"
                  name="poNumber"
                  rules={[{ required: true, message: "Please enter order number" }]}>
                  <Input size="large" />
                </Form.Item>
                <Form.Item label="Order Date"
                  className="flex-1"
                  name="poDate"
                  rules={[{ required: true, message: "Please enter order date" }]}>
                  <CustomDateTimePicker
                    type="date"
                    form={form}
                    itemName="poDate"
                    className="w-full h-[32px]"
                  />
                </Form.Item>
              </Flex>
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Supplier"
                    name="supplierID"
                    className="w-full"
                    rules={[{ required: true, message: "Please select supplier" }]}>
                    <SupplierOrgSelect />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Warehouse"
                    name="warehouseID"
                    className="w-full"
                    rules={[{ required: true, message: "Please select warehouse" }]}>
                    <WarehouseSelect />
                  </Form.Item>
                </Col>
              </Row>
              {purchaseOrderId && (
                <>
                  <Flex gap={8}>
                    <Form.Item label="Port of Loading" className="w-full">
                      <div className="font-medium text-sm flex justify-start items-center">
                        {currentPo?.portOfLoadingCode ? (
                          <>
                            <ReactCountryFlag
                              countryCode={currentPo?.portOfLoadingCode
                                .substring(0, 2)}
                              svg
                              className="!w-7 !h-6"
                            />
                            <span className="ml-2">
                              {currentPo?.portOfLoading}
                            </span>{" "}
                          </>
                        ) : (
                          <div className="w-6 h-5 mr-2 flex-shrink-0"></div>
                        )}
                      </div>
                    </Form.Item>
                    <Form.Item label="Port of Discharge" className="w-full">
                      <div className="font-medium text-sm flex justify-start items-center">
                        {currentPo?.portOfDischargeCode ? (
                          <>
                            <ReactCountryFlag
                              countryCode={currentPo?.portOfDischargeCode
                                .substring(0, 2)}
                              svg
                              className="!w-7 !h-6"
                            />
                            <span className="ml-2">
                              {currentPo?.portOfDischarge}
                            </span>
                          </>
                        ) : (
                          <div className="w-6 h-5 mr-2 flex-shrink-0"></div>
                        )}
                      </div>
                    </Form.Item>
                  </Flex>
                  <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Cargo Ready Date"
                        name="cargoReadyDate">
                        <CustomDateTimePicker
                          type="date"
                          form={form}
                          itemName="cargoReadyDate"
                          className={`h-[32px] ${isSmallPhone ? "" : "w-full"}`}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item label="ETA"
                        name="etaDate">
                        <CustomDateTimePicker
                          type="date"
                          form={form}
                          itemName="etaDate"
                          className={`h-[32px] ${isSmallPhone ? "" : "w-full"}`}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
              <Flex gap={8}>
                <Form.Item label="Contents"
                  className="flex-1"
                  rules={[
                    {
                      required: true,
                      message: "Please enter contents",
                    },
                    {
                      max: 20,
                      message: "Contents must be 20 characters or less",
                    },
                  ]}
                  name="contentSummary">
                  <Input size="large" placeholder="Max 20 characters" />
                </Form.Item>
                <Form.Item label="Is urgent?" name="isUrgent">
                  <Switch size="large" />
                </Form.Item>
              </Flex>

            </Card>
          </Col>
          <Col span={12} xs={24} md={24} lg={12}>
            <Card className="card-form flex-1 h-full">
              <Row gutter={[8, 8]} className="flex-1">
                <Col xs={12} md={8}>
                  <Form.Item label="Order Total"
                    className="flex-1"
                    name={["poTotal", "amount"]}
                    rules={[{ required: true, message: "Please enter order amount" }]}>
                    <Input size="large" type="number" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item label="Currency"
                    className="flex-1"
                    name={["poTotal", "currencyCode"]}
                    rules={[{ required: true, message: "Please enter currency code" }]}>
                    <CurrencyCodeSelect size="medium" />
                  </Form.Item>
                </Col>
                {purchaseOrderId &&
                  <Col xs={24} md={8}>
                    <Form.Item label="Status">
                      <div className="font-medium text-sm mt-2">
                        {currentStatus || !purchaseOrderId ? currentStatus : "Cancelled"}
                      </div>
                    </Form.Item>
                  </Col>
                }
              </Row>

              <Form.Item label="Notes" name="notes">
                <Input.TextArea size="large" rows={2} />
              </Form.Item>
              {purchaseOrderId &&
                <>
                  <Flex gap={8}>
                    <Form.Item label="QA Date"
                      name="qaDate"
                    >
                      <CustomDateTimePicker
                        type="date"
                        form={form}
                        itemName="qaDate"
                        className={`h-[32px] ${isSmallPhone ? "" : "w-full"}`}
                      />
                    </Form.Item>
                    <Form.Item label={`${isSmallPhone ? "" : "QA"} Completed?`} name="isQACompleted">
                      <Switch size="large" />
                    </Form.Item>
                  </Flex>
                </>
              }
              {purchaseOrderId &&
                <PurchaseOrderUpdateStatusButtons purchaseOrderId={purchaseOrderId} />
              }
            </Card>
          </Col>
        </Row>
        <Flex justify="end" align="center" gap={8} className="mt-4">
          <AppButton variant="save" className="button-primary" type="primary" onClick={handleSubmit}>Save</AppButton>
          {purchaseOrderId &&
            <Tooltip
              title={currentStatus !== "Submitted" ?
                "This PO has been Sent to Supplier and can not be deleted. You may only Cancel this PO." : null}>
              <PurchaseOrderDelete disabled={currentStatus !== "Submitted"} purchaseOrderId={purchaseOrderId} />
            </Tooltip>
          }
          <AppButton variant="close" className="button-secondary" onClick={handleCancel}>Close</AppButton>
        </Flex>
      </Form>

      {purchaseOrderId &&
        <PurchaseOrderDocumentStatusPage
          purchaseOrderId={purchaseOrderId}
          currentPo={currentPo}
          purchaseOrderLineItems={purchaseOrderLineItems}
          handlePoLineItemChange={handlePoLineItemChange}
          handleAddNewPoLineItem={handleAddNewPoLineItem}
          handlePoLineItemDelete={handlePoLineItemDelete}
          onImportPurchaseOrderLineItems={handleImportPurchaseOrderLineItems}
        />
      }
    </div>
  );
};

export default PurchaseOrderDetailsPage;